// @ts-nocheck
import Swiper from '@theme/lib-swiper';

class ImageSlider {
  constructor(container) {
    this.container = container;
    this.pagination = container.querySelector('.image-slider__pagination');
    this.prevButton = container.querySelector('.image-slider__button--prev');
    this.nextButton = container.querySelector('.image-slider__button--next');
    this.swiperInstance = null;
    this.hasMagnifier = container?.dataset.imageSliderMagnifier === 'true';
    this.activeTouchPointerId = null;
  }

  isValid() {
    return (
      this.container && this.pagination && this.prevButton && this.nextButton
    );
  }

  isReady() {
    return this.container.dataset.sliderReady === 'true';
  }

  markReady() {
    this.container.dataset.sliderReady = 'true';
  }

  clampPercentage(value) {
    return Math.min(Math.max(value, 0), 100);
  }

  updateZoomPosition(slide, event) {
    const rect = slide.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    slide.style.setProperty(
      '--image-slider-zoom-x',
      `${this.clampPercentage(x)}%`
    );
    slide.style.setProperty(
      '--image-slider-zoom-y',
      `${this.clampPercentage(y)}%`
    );
  }

  setSwiperTouchMove(enabled) {
    if (!this.swiperInstance) {
      return;
    }

    this.swiperInstance.allowTouchMove = enabled;
  }

  deactivateZoom(slide) {
    if (!slide) {
      return;
    }

    slide.classList.remove('is-zooming');
    slide.style.removeProperty('--image-slider-zoom-x');
    slide.style.removeProperty('--image-slider-zoom-y');
    this.activeTouchPointerId = null;

    if (
      !this.container.querySelector(
        '.image-slider__slide--magnifier.is-zooming'
      )
    ) {
      this.setSwiperTouchMove(true);
    }
  }

  deactivateAllZoomSlides(exceptSlide = null) {
    const zoomingSlides = this.container.querySelectorAll(
      '.image-slider__slide--magnifier.is-zooming'
    );

    zoomingSlides.forEach((slide) => {
      if (slide !== exceptSlide) {
        this.deactivateZoom(slide);
      }
    });
  }

  activateZoom(slide, event) {
    this.deactivateAllZoomSlides(slide);
    this.updateZoomPosition(slide, event);
    slide.classList.add('is-zooming');
    this.setSwiperTouchMove(false);
  }

  bindMagnifier() {
    if (
      !this.hasMagnifier ||
      this.container.dataset.magnifierReady === 'true'
    ) {
      return;
    }

    const zoomSlides = this.container.querySelectorAll(
      '[data-image-slider-zoom-slide]'
    );

    if (!zoomSlides.length) {
      return;
    }

    zoomSlides.forEach((slide) => {
      let lastPointerType = 'mouse';
      let touchMoved = false;
      let touchStartX = 0;
      let touchStartY = 0;

      const handlePointerMove = (event) => {
        lastPointerType = event.pointerType || lastPointerType;

        if (event.pointerType === 'touch' || event.pointerType === 'pen') {
          if (
            this.activeTouchPointerId !== null &&
            event.pointerId !== this.activeTouchPointerId
          ) {
            return;
          }

          if (this.activeTouchPointerId === event.pointerId) {
            const deltaX = Math.abs(event.clientX - touchStartX);
            const deltaY = Math.abs(event.clientY - touchStartY);

            if (deltaX > 8 || deltaY > 8) {
              touchMoved = true;
            }
          }

          if (!slide.classList.contains('is-zooming')) {
            return;
          }

          this.updateZoomPosition(slide, event);
          event.preventDefault();
          return;
        }

        this.updateZoomPosition(slide, event);
        slide.classList.add('is-zooming');
      };

      slide.addEventListener('pointerenter', (event) => {
        lastPointerType = event.pointerType || lastPointerType;

        if (event.pointerType === 'touch' || event.pointerType === 'pen') {
          return;
        }

        this.updateZoomPosition(slide, event);
        slide.classList.add('is-zooming');
      });

      slide.addEventListener('pointerdown', (event) => {
        lastPointerType = event.pointerType || lastPointerType;

        if (event.pointerType !== 'touch' && event.pointerType !== 'pen') {
          return;
        }

        this.activeTouchPointerId = event.pointerId;
        touchMoved = false;
        touchStartX = event.clientX;
        touchStartY = event.clientY;

        if (slide.classList.contains('is-zooming')) {
          this.updateZoomPosition(slide, event);
          event.preventDefault();
        }
      });

      slide.addEventListener('pointermove', handlePointerMove);
      slide.addEventListener('pointerup', (event) => {
        if (this.activeTouchPointerId === event.pointerId) {
          this.activeTouchPointerId = null;
        }
      });
      slide.addEventListener('pointercancel', (event) => {
        if (this.activeTouchPointerId === event.pointerId) {
          this.activeTouchPointerId = null;
        }
      });
      slide.addEventListener('click', (event) => {
        if (lastPointerType !== 'touch' && lastPointerType !== 'pen') {
          return;
        }

        if (touchMoved) {
          touchMoved = false;
          return;
        }

        if (slide.classList.contains('is-zooming')) {
          this.deactivateZoom(slide);
          return;
        }

        this.activateZoom(slide, event);
      });
      slide.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch' || event.pointerType === 'pen') {
          return;
        }

        this.deactivateZoom(slide);
      });
    });

    document.addEventListener('pointerdown', (event) => {
      if (this.container.contains(event.target)) {
        return;
      }

      this.deactivateAllZoomSlides();
    });

    this.container.dataset.magnifierReady = 'true';
  }

  initThumbnails() {
    const galleryRoot = this.container.closest('.product-gallery');
    if (!galleryRoot) return;

    const thumbnailsContainer = galleryRoot.querySelector('[data-thumbnails]');
    if (!thumbnailsContainer) return;

    const thumbnails = Array.from(
      thumbnailsContainer.querySelectorAll('[data-thumbnail-index]')
    );
    if (!thumbnails.length) return;

    thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', () => {
        const index = parseInt(thumbnail.dataset.thumbnailIndex, 10);
        if (this.swiperInstance && !Number.isNaN(index)) {
          this.swiperInstance.slideTo(index);
        }
      });
    });

    const updateActive = () => {
      const activeIndex = this.swiperInstance?.activeIndex || 0;
      thumbnails.forEach((thumb, idx) => {
        thumb.classList.toggle('is-active', idx === activeIndex);
      });
    };

    if (this.swiperInstance) {
      this.swiperInstance.on('slideChange', updateActive);
      updateActive();
    } else {
      // If swiper isn't initialized yet, set a short timeout to attempt syncing
      setTimeout(() => {
        if (this.swiperInstance) {
          this.swiperInstance.on('slideChange', updateActive);
          updateActive();
        }
      }, 50);
    }
  }

  init() {
    if (this.isReady()) {
      return;
    }

    if (
      !this.container ||
      !this.pagination ||
      !this.prevButton ||
      !this.nextButton
    ) {
      return;
    }

    this.pagination.classList.add('swiper-pagination-bullets');

    const slideCount = this.container.querySelectorAll('.swiper-slide').length;

    this.swiperInstance = new Swiper(this.container, {
      loop: slideCount > 1,
      keyboard: { enabled: true },
      navigation: {
        nextEl: this.nextButton,
        prevEl: this.prevButton,
      },
      pagination: {
        el: this.pagination,
        clickable: true,
      },
      observer: true,
      observeParents: true,
    });

    this.container.swiperInstance = this.swiperInstance;
    this.initThumbnails();

    this.swiperInstance.on('slideChangeTransitionStart', () => {
      this.deactivateAllZoomSlides();
    });

    this.bindMagnifier();

    this.markReady();
  }
}

function initAllImageSliders() {
  const sliders = document.querySelectorAll(
    '[data-image-slider], .image-slider'
  );

  if (!sliders.length) {
    return;
  }

  sliders.forEach((slider) => {
    const instance = new ImageSlider(slider);
    instance.init();
  });
}

if (!window.__imageSliderBooted) {
  window.__imageSliderBooted = true;

  document.addEventListener('DOMContentLoaded', initAllImageSliders);
  document.addEventListener('shopify:section:load', initAllImageSliders);
  document.addEventListener('shopify:section:reorder', initAllImageSliders);
  document.addEventListener('shopify:section:select', initAllImageSliders);
  document.addEventListener('shopify:block:select', initAllImageSliders);

  document.addEventListener('dialog:open', () => {
    setTimeout(() => initAllImageSliders(), 0);
  });
}

initAllImageSliders();
